import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectCaseStudy.module.css';

export default function WeatherSatellitePage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.articleContainer}>

      <button className={styles.backButton} onClick={() => navigate('/projects')}>
        ← Back to Projects
      </button>

      <header className={styles.header}>
        <h1 className={styles.title}>
          Picosatellite Software & Picosatellite Software & Automated Weather Satellite Ground Station
        </h1>
        <div className={styles.authors}>
          <span className={styles.highlightAuthor}>Jolok Banarjee (Team Member — Picosatellite Software & Ground Station)</span>
        </div>
        <div className={styles.affiliations}>
          Supervised by: Prof. Nazmul Ula (Loyola Marymount University, USA) & Asst. Prof. Md. Samin Rahman (AAUB)
        </div>
        <div className={styles.badge}>
          📡 PicoSat4Bangladesh Initiative &nbsp;•&nbsp; Mar 2022 – February 2023
        </div>
      </header>

      {/* Hero Image */}
      <img
        src="/assets/project/Automated Weather Satellite Ground Station/cover.png"
        alt="Weather Satellite Ground Station Dashboard"
        className={styles.heroImage}
      />

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Project Overview</h3>
        <p className={styles.abstractText}>
          Initiated under <strong>PicoSat4Bangladesh</strong>, an international collaboration between Loyola Marymount University (LMU), USA and Bangabandhu Sheikh Mujibur Rahman Aviation and Aerospace University (BSMRAAU) current name is Aviation and Aerospace University, Bangladesh (AAUB), this project develops an <strong>Automated Weather Satellite Ground Station</strong> designed to receive, process, and visualize NOAA weather satellite data seamlessly. The system leverages <strong>Amazon AWS cloud servers</strong>, automatically uploading decoded APT signals to an <strong>S3 bucket</strong> for real-time, global access to meteorological imagery.
        </p>
        <div className={styles.keywords}>
          <strong>Focus Areas:</strong> Cloud Architecture, AWS S3, Satellite Communications, Software-Defined Radio (SDR), Python, Remote Sensing.
        </div>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>Signal Acquisition & Automation</h2>
        <p className={styles.paragraph}>
          Tracking Low Earth Orbit (LEO) satellites demands precise orbital prediction and automated radio tuning. The ground station employs a V-Dipole antenna array coupled with a Software-Defined Radio (SDR) receiver to capture 137 MHz VHF signals as NOAA satellites (NOAA-15, 18, and 19) pass overhead.
        </p>
        <p className={styles.paragraph}>
          Automated scheduling scripts running on a local Raspberry Pi predict satellite passes. During each pass, the system autonomously records audio telemetry and decodes the APT signal into high-resolution meteorological imagery — all without manual intervention.
        </p>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>AWS Cloud Infrastructure</h2>
        <p className={styles.paragraph}>
          To make decoded weather imagery accessible to researchers in real-time, I designed the cloud data pipeline. Local storage limitations on edge devices make long-term meteorological analysis impractical, necessitating a robust, scalable cloud architecture.
        </p>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>137 MHz</div>
            <div className={styles.metricLabel}>VHF Reception</div>
            <p className={styles.metricSubtext}>
              Tuned for NOAA polar-orbiting satellites.
            </p>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricValue}>AWS S3</div>
            <div className={styles.metricLabel}>Cloud Pipeline</div>
            <p className={styles.metricSubtext}>
              Automated image upload, archiving & public hosting.
            </p>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricValue}>24/7</div>
            <div className={styles.metricLabel}>Autonomous Uptime</div>
            <p className={styles.metricSubtext}>
              Zero manual tracking or decoding required.
            </p>
          </div>
        </div>

        <p className={styles.paragraph}>
          The ground station software uploads processed satellite images and metadata immediately to an <strong>Amazon S3 Bucket</strong> configured for static website hosting. This cloud-first approach ensures high-resolution weather data is securely archived, globally accessible, and instantly available for web-based visualization dashboards.
        </p>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Team & Collaboration</h2>
        <p className={styles.paragraph}>
          The PicoSat4Bangladesh initiative brought together students and faculty from both LMU and BSMRAAU. As a Software Team Member, I contributed to the cloud infrastructure, S3 bucket configuration, IAM policy setup, and the automated data pipeline. Below are moments from our team meetings and project sessions.
        </p>

        <div className={styles.galleryGrid}>
          <figure className={styles.figureCard}>
            <img
              src="/assets/project/Automated Weather Satellite Ground Station/team_meeting_with_ulla.jpg"
              alt="Team meeting with Prof. Nazmul Ula"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>
              Team meeting with Prof. Nazmul Ula (LMU)
            </figcaption>
          </figure>
          <figure className={styles.figureCard}>
            <img
              src="/assets/project/Automated Weather Satellite Ground Station/with_team_members.jpeg"
              alt="PicoSat4Bangladesh team members"
              className={styles.galleryImage}
            />
            <figcaption className={styles.galleryCaption}>
              PicoSat4Bangladesh team at BSMRAAU
            </figcaption>
          </figure>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Resources & Documentation</h2>
        <p className={styles.paragraph}>
          I prepared a comprehensive, step-by-step guide for creating an AWS S3 bucket, configuring IAM users, enabling static website hosting, and setting up public access policies — specifically tailored for this ground station deployment.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', margin: '1.5rem 0' }}>
          <a
            href="https://github.com/jolok-banarjee/Automated-Weather-Satellite-Ground-Station"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.btnPrimary}
          >
            <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
            View on GitHub
          </a>

          <a
            href="https://drive.google.com/file/d/1gAhNx5cQOwb72WemgnQWIUb5WzBHnRwB/view?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.7rem 1.35rem',
              background: 'var(--card)',
              color: 'var(--fg)',
              border: '1px solid var(--border-strong)',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: 650,
              fontSize: '0.9rem',
              boxShadow: 'var(--shadow-xs)'
            }}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
            S3 Bucket Guide (PDF)
          </a>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Conclusion</h2>
        <p className={styles.paragraph}>
          This project successfully demonstrated the integration of low-cost hardware with enterprise-grade cloud architecture to democratize access to space-based data. It served as a critical foundational step for the PicoSat4Bangladesh initiative, proving the team's capability to manage ground-to-space communications, automated data pipelines, and real-time remote sensing workflows.
        </p>
        <p className={styles.paragraph}>
          By contributing to <strong>remote sensing</strong>, <strong>satellite data processing</strong>, and <strong>cloud-based weather monitoring</strong>, this system lays the groundwork for future Bangladeshi pico-satellite missions and open-access meteorological research.
        </p>
      </section>

    </div>
  );
}